import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { UserData } from "@/types/auth";
import type { ApiError } from "@/api/auth";
import { Loader2 } from "lucide-react";

const userSchema = z.object({
  first_name: z.string().min(2, "First name must be at least 2 characters"),
  last_name: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone_number: z.string()
  .regex(/^(10|11|12|15)\d{8}$/, "Invalid phone number format")
  .transform(val => `+20${val}`),
  password: z.string().min(8, "Password must be at least 8 characters"),
  role: z.string().min(1, "Role is required"),
});

interface UserFormProps {
  onNext: (data: UserData) => void;
  apiError?: ApiError;
  isLoading?: boolean;
}

export function UserForm({ onNext, apiError, isLoading }: UserFormProps) {
  const form = useForm<UserData>({
    resolver: zodResolver(userSchema),
  });

  React.useEffect(() => {
    if (apiError?.error?.detail) {
      Object.entries(apiError.error.detail).forEach(([field, errors]) => {
        form.setError(field as keyof UserData, {
          type: 'server',
          message: Array.isArray(errors) ? errors[0] : errors as string
        });
      });
    }
  }, [apiError, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onNext)} className="space-y-6">
        <fieldset disabled={isLoading} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                      <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                              +20
                          </div>
                          <Input
                              {...field}
                              className="pl-11"
                              value={field.value?.replace(/^\+20/, '') || ''}
                              onChange={(e) => {
                                  const value = e.target.value.replace(/[^\d]/g, '');
                                  field.onChange(value);
                              }}
                          />
                      </div>
                  </FormControl>
                  <FormMessage/>
              </FormItem>
            )}
          />

            <FormField
                control={form.control}
                name="password"
                render={({field}) => (
                    <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                            <Input type="password" {...field} />
                        </FormControl>
                        <FormMessage/>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Registering...
            </>
          ) : (
            "Next"
          )}
        </Button>
      </form>
    </Form>
  );
}
